import React from 'react';
import PropTypes from 'prop-types';
import {
  View,
  Text,
  TouchableWithoutFeedback,
} from 'react-native';

import BookButton from '../BookButton';
import {
  ServicePropType,
  ServiceGroupPropType,
  PromotionPropType,
} from '../ServiceList/ServiceListPropTypes';

import styles from './PromotedServiceItem.module.scss';

/**
 * Renders a promoted service item, which emphasizes details of the promotion over the details of
 * the service.
 */
const PromotedServiceItem = ({
  service,
  promotion,
  isDisabled,
  isEditMode,
  allowOnlineBooking,
  providerId,
  providerPhone,
  index,
  group,
  description,
  isExpanded,
  onInfoClick,
  bookText,
  highlightedServiceId,
}) => {
  const MAX_LENGTH = 80;
  const hasDescription = !!description;
  const truncate = text => (text.length > MAX_LENGTH ? `${text.substring(0, MAX_LENGTH)}...` : text);

  const bookButtonContainerClasses = [styles.bookButtonContainer];

  return (
    <React.Fragment>
      <View styleName="serviceContainer">
        <View styleName="serviceInfo">
          <View
            accessibilityRole="banner"
            styleName="header"
          >
            <Text styleName="promoAmount">{`${promotion.formattedDiscount} off`}</Text>
            <Text
              accessibilityRole="heading"
              aria-level="3"
              styleName="serviceTitle"
            >
              {promotion.name}
            </Text>
          </View>
          <View styleName="summary">
            <Text styleName="serviceDetails">{service.name}</Text>
          </View>
          <View styleName="promoDateRange">
            <Text styleName="promoDateText">{promotion.startTime.format('MM/DD/YYYY')}</Text>
            <Text styleName="promoDateText">&nbsp;-&nbsp;</Text>
            <Text styleName="promoDateText">{promotion.endTime.format('MM/DD/YYYY')}</Text>
          </View>
          <View styleName="descriptionContainer">
            {
              hasDescription && (
                <View styleName="itemDescription">
                  <Text styleName="blurb">{isExpanded ? description : truncate(description)}</Text>
                  {description.length > MAX_LENGTH ? (
                    <TouchableWithoutFeedback onPress={onInfoClick}>
                      <View>
                        <Text styleName="readMore">{`${isExpanded ? 'Read Less' : 'Read More'}`}</Text>
                      </View>
                    </TouchableWithoutFeedback>
                  ) : null}
                </View>
              )
            }
          </View>

        </View>
        {!isDisabled && (
          <View className={bookButtonContainerClasses}>
            <BookButton
              isEditMode={isEditMode}
              isOnlineBookable={allowOnlineBooking && service.is_online_bookable}
              service={service}
              promotion={promotion}
              providerId={providerId}
              providerPhone={providerPhone}
              index={index}
              group={group}
              bookText={bookText}
              highlightedServiceId={highlightedServiceId}
            />
          </View>
        )}
      </View>
    </React.Fragment>
  );
};

PromotedServiceItem.propTypes = {
  /** True if edit mode is enabled */
  isEditMode: PropTypes.bool,
  /** The service to book */
  service: ServicePropType.isRequired,
  /** The provider associated with the service */
  providerId: PropTypes.number.isRequired,
  /** The phone number of the pro */
  providerPhone: PropTypes.string,
  /** The promotion associated with the service, if any */
  promotion: PromotionPropType.isRequired,
  /** The overall index of the service in the list, used for tracking */
  index: PropTypes.number.isRequired,
  /** The service group, used for tracking */
  group: ServiceGroupPropType.isRequired,
  /** True if the item should be disabled, otherwise false */
  isDisabled: PropTypes.bool,
  /** True if the profile supports online booking, otherwise false */
  allowOnlineBooking: PropTypes.bool,
  /** True if the description area should be expanded */
  isExpanded: PropTypes.bool,
  /** The description to display */
  description: PropTypes.string,
  /** The function called when the info icon is clicked */
  onInfoClick: PropTypes.func,
  /** Text to use on book button when online booking is enabled */
  bookText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
  ]),
  /** Matched service ID from search card */
  highlightedServiceId: PropTypes.number,
};

PromotedServiceItem.defaultProps = {
  isDisabled: false,
  isEditMode: false,
  allowOnlineBooking: false,
  providerPhone: null,
  isExpanded: false,
  description: null,
  onInfoClick: null,
  bookText: 'See Times',
  highlightedServiceId: null,
};

export default PromotedServiceItem;
